import { Graphics, ResourceBatch } from 'ohzi-core';
import { RenderLoop } from 'ohzi-core';
import { Configuration } from 'ohzi-core';
import { EventManager } from 'ohzi-core';
import { Debug } from 'ohzi-core';
import { Initializer } from 'ohzi-core';
import { ResourceContainer } from 'ohzi-core';
import Loader from './Loader';

// APP
import MainApplication from './MainApplication';

class AppApi
{
  init()
  {
    this.application = new MainApplication();
    this.loader = new Loader(this);

    this.render_loop = new RenderLoop(this.loader, Graphics);
    this.config = Configuration;

    let app_container = document.querySelector('.webgl-canvas');
    let canvas = document.querySelector('.webgl-canvas');

    Initializer.init(canvas, app_container, {
      antialias: true,
      logarithmicDepthBuffer: true,
    });
    Configuration.dpr = 1;//window.devicePixelRatio;
    this.application.init(Graphics);
    this.updateLabelsCallback = undefined;

    // --- Adaptive rendering: idle → stopped, active → 60fps ---
    this._idleTimeout = null;
    this._idleDelay = 2000; // stop after 2s of inactivity
    this._mainAppReady = false;

    // Pause when tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._goIdle(true);
      } else {
        this.wake();
      }
    });
  }

  // Wake the render loop and schedule idle timeout
  wake(duration) {
    if (!this._mainAppReady) return;
    if (document.hidden) return;

    if (!this.render_loop.is_running) {
      this.render_loop.start();
    }

    clearTimeout(this._idleTimeout);
    this._idleTimeout = setTimeout(() => this._goIdle(false), duration || this._idleDelay);
  }

  _goIdle(force) {
    clearTimeout(this._idleTimeout);
    if (this.render_loop.is_running) {
      this.render_loop.stop();
    }
  }

  // Bind canvas interaction listeners (called once after canvas is ready)
  _bindCanvasInteraction() {
    const canvas = document.querySelector('.webgl-canvas');
    if (!canvas) return;

    const onInteract = () => this.wake();

    canvas.addEventListener('mousedown', onInteract);
    canvas.addEventListener('mousemove', onInteract);
    canvas.addEventListener('wheel', onInteract, { passive: true });
    canvas.addEventListener('touchstart', onInteract, { passive: true });
    canvas.addEventListener('touchmove', onInteract, { passive: true });

    // Also wake on window resize (scene needs re-render)
    window.addEventListener('resize', () => this.wake(500));
  }

  load(settings = {})
  {
    this.init();

    window.app = this.application;
    window.ViewApi = this;
    window.settings = settings;

    this.loader.load();

    for (const [key, value] of Object.entries(settings)) 
    {
      Configuration[key] = value;
    }

  }

  dispose()
  {
    this.application.dispose();
    Initializer.dispose(this.render_loop);
  }

  draw_debug_axis()
  {
    Debug.draw_axis();
  }

  register_event(name, callback)
  {
    EventManager.on(name, callback);
  }

  set_resource(name, resource)
  {
    ResourceContainer.set_resource(name, resource);
  }

  set_settings(settings)
  {
    window.settings = settings;
  }

  start_main_app()
  {
    this.render_loop.set_state(this.application);
  }

  start()
  {
    this.render_loop.start();
    this._mainAppReady = true;
    this._bindCanvasInteraction();
    // Go idle after initial render settles
    this.wake(3000);
  }

  stop()
  {
    this.render_loop.stop();
  }

  take_screenshot(callback)
  {
    Graphics.take_screenshot(callback);
  }

  download_blob(blob)
  {
    Graphics.download_screenshot(blob);
  }

  setSun(aBoolean)
  {
    this.application.setSun(aBoolean);
  } 

  updateLabels(frg_w, og_w, mg_w, so_w, sm_w, u_value, g_o, g_m, rotate_frg)
  {
    // Wake render loop for the structure animation (~1000ms lerp + 100ms debounce + buffer)
    this.wake(1500);

    // the function that sets the timeout and the important function call in the end
    const timeout = () =>
    {
      return setTimeout( (() =>
      {
        // what wants to be called
        this.application.updateLabels(frg_w, og_w, mg_w, so_w, sm_w, u_value, g_o, g_m, rotate_frg);
      }).bind(this), 100 );
    };

    // we damp the call
    if( this.updateLabelsCallback === undefined)
    {
      this.updateLabelsCallback = timeout();
    }
    else
    {
      clearTimeout(this.updateLabelsCallback);
      this.updateLabelsCallback = timeout();
    }
  }
}

const Api = new AppApi();
export { Api, ResourceBatch };
